import { useEffect, useState } from "react";

import { create } from "@bufbuild/protobuf";

import { GetArtifactDownloadUrlRequestSchema } from "@gx/ts-contracts/services/ontology/api/artifact_pb";

import { useGetArtifactDownloadUrlMutation } from "@/api/mutations/ontology";

interface UseArtifactContentState {
  content: string | null;
  isLoading: boolean;
  error: Error | null;
  downloadUrl: string | null;
}

const DEFAULT_STATE: UseArtifactContentState = {
  content: null,
  downloadUrl: null,
  isLoading: false,
  error: null,
};

/**
 * Hook for fetching artifact content from S3.
 * Handles getting presigned URL and fetching content in one place.
 */
export const useArtifactContent = (artifactUri: string | undefined): UseArtifactContentState => {
  const { mutateAsync: getDownloadUrl } = useGetArtifactDownloadUrlMutation();
  const [state, setState] = useState<UseArtifactContentState>(DEFAULT_STATE);

  useEffect(() => {
    if (!artifactUri) {
      setState(DEFAULT_STATE);
      return;
    }

    let cancelled = false;

    const fetchContent = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        // Get presigned URL
        const result = await getDownloadUrl(
          create(GetArtifactDownloadUrlRequestSchema, {
            artifactUri,
          }),
        );

        if (cancelled) return;

        if (!result?.url) {
          throw new Error("No download URL returned");
        }

        setState((prev) => ({ ...prev, downloadUrl: result.url }));

        // Fetch content from presigned URL
        const response = await fetch(result.url);
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
        }

        if (cancelled) return;

        const text = await response.text();
        setState((prev) => ({ ...prev, content: text, isLoading: false }));
      } catch (err) {
        if (cancelled) return;
        const fetchError =
          err instanceof Error ? err : new Error("Failed to fetch artifact content");
        setState((prev) => ({ ...prev, error: fetchError, isLoading: false }));
        console.error("Failed to fetch artifact content:", fetchError);
      }
    };

    void fetchContent();

    return () => {
      cancelled = true;
    };
  }, [artifactUri, getDownloadUrl]);

  return {
    content: state.content,
    isLoading: state.isLoading,
    error: state.error,
    downloadUrl: state.downloadUrl,
  };
};
